
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import { withLDProvider, LDEvaluationDetail, LDOptions } from 'launchdarkly-react-client-sdk';
import Head from 'next/head';
import { ComponentType, useEffect } from 'react';
import { colorBorder, AdsConfigProvider, gray10 } from '@arkestro/arkestro-design-system';
import { datadogRum } from '@datadog/browser-rum';
import '@utils/browserTranslateDomPatch'; // MUST be loaded before any React render so browser-translation DOM mutations do not crash the reconciler.
import '@utils/dayjsInit'; // PEPITA-825: MUST be loaded before App so dayjs plugins are extended before any page chunk evaluates a `.tz()` call.
import '@styles/globals.scss'; // Do not move this after importing App, it will override the App sass styles
import { QueryClientProvider } from '@tanstack/react-query';
import App from '@components/App/App'; // This must be after global styles, so it can override appropriately
import { ConfigurationsProvider } from '@components/providers/ConfigurationsProvider';
import UserProvider from '@components/providers/UserProvider/UserProvider';
import PageErrorBoundary from '@components/shared/ErrorBoundaries/PageErrorBoundary';
import AppConfig from '@config/AppConfig';
import { useChunkReload } from '@hooks/useChunkReload';
import AxiosService from '@services/AxiosService';
import IntercomService from '@services/IntercomService';
import { queryClient } from '@services/QueryClient';
import type { AppProps } from 'next/app'; // Not clear why ESLint wants this here?
export const PRIMARY_WINDOW_NAME = 'ArkestroAppWindow';
// Setup defaults and refresh token handling
AxiosService.setup();
const AppPage = ({ Component, pageProps }: AppProps) => {
    // Recover from stale Next.js chunks after a deploy by hard-reloading on
    // chunk-load errors. See DEVX-54.
    useChunkReload();
    /* Used for SSO Auth redirects and similar */
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.name = PRIMARY_WINDOW_NAME;
        }
    }, []);
    useEffect(() => {
        IntercomService.initialize();
    }, []);
    return (<>
      {/* Needed to isolate antd v5 styles from antd v3 for v5 components. */}
      <AdsConfigProvider theme={{
            components: {
                Badge: {
                    dotSize: 8,
                },
                Checkbox: {
                    colorBorder,
                    colorPrimary: gray10,
                    colorPrimaryHover: gray10,
                },
                Collapse: {
                    headerBg: 'white',
                },
                InputNumber: {
                    addonBg: 'white',
                },
            },
        }}>
        <Head>
          <meta content="noindex" name="robots"/>
          <meta content="width=device-width, initial-scale=1.0" name="viewport"/>
        </Head>
        <PageErrorBoundary>
          <ConfigurationsProvider>
            <QueryClientProvider client={queryClient}>
              <UserProvider>
                <App Component={Component} pageProps={pageProps}/>
              </UserProvider>
            </QueryClientProvider>
          </ConfigurationsProvider>
        </PageErrorBoundary>
      </AdsConfigProvider>
    </>);
};
const context = {
    // Explicitly set "anonymous" when not authenticated.
    // Handles false MAUs in Synthetics.
    key: 'unauthenticated',
    kind: 'user',
};
const options: LDOptions = {
    // Default for this is false, but we use true when we need a reason why a flag is on/off.
    // https://docs.launchdarkly.com/sdk/concepts/evaluation-reasons#understanding-the-reason-data
    evaluationReasons: false,
    inspectors: [
        {
            method: (key: string, detail: LDEvaluationDetail) => {
                datadogRum.addFeatureFlagEvaluation(key, detail.value);
            },
            name: 'dd-inspector',
            type: 'flag-used'
        }
    ]
};
const __Next_Translate__Page__1a1a258beee__ = withLDProvider({
    clientSideID: AppConfig.launchDarklyClientSideID!,
    context,
    options,
})(AppPage as ComponentType);

    export default __appWithI18n(__Next_Translate__Page__1a1a258beee__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  